import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";

import { logout } from "@/api/endpoints";
import { InfoTip } from "@/components/InfoTip";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/hooks/useAuth";
import { useIdleLogout } from "@/hooks/useIdleLogout";

function IdleWarningModal({ onStay }: { onStay: () => void }): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="idle-title"
    >
      <div className="card max-w-sm w-full space-y-4">
        <h2 id="idle-title" className="text-lg font-semibold">
          Still there?
        </h2>
        <p className="text-sm text-ink-300">
          You&apos;ve been inactive for a while. For security you&apos;ll be signed
          out automatically in about two minutes unless you continue.
        </p>
        <button type="button" className="btn-primary w-full" onClick={onStay} autoFocus>
          Stay signed in
        </button>
      </div>
    </div>
  );
}

export function AppShell(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();

  async function onLogout(): Promise<void> {
    await logout();
    navigate("/login", { replace: true });
  }

  const idle = useIdleLogout(() => void onLogout());

  return (
    <div className="min-h-full flex flex-col">
      <header className="bg-ink-800 ring-1 ring-ink-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-x-6 gap-y-2 flex-wrap">
          <Link to="/" className="font-semibold tracking-tight">
            AquaTrack
          </Link>
          <nav className="flex items-center gap-x-4 gap-y-1 text-sm flex-wrap">
            {auth.scope === "tenant" && (
              <NavLink
                to="/home"
                className={({ isActive }) =>
                  isActive ? "text-ink-100" : "text-ink-300 hover:text-ink-100"
                }
              >
                Home
              </NavLink>
            )}
            {auth.scope === "platform" && (
              <NavLink
                to="/platform"
                className={({ isActive }) =>
                  isActive ? "text-ink-100" : "text-ink-300 hover:text-ink-100"
                }
              >
                Platform
              </NavLink>
            )}
            <NavLink
              to="/systems"
              className={({ isActive }) =>
                isActive ? "text-ink-100" : "text-ink-300 hover:text-ink-100"
              }
            >
              Systems
            </NavLink>
            <NavLink
              to="/compliance/nitrate-nitrite"
              className={({ isActive }) =>
                isActive ? "text-ink-100" : "text-ink-300 hover:text-ink-100"
              }
            >
              Compliance
            </NavLink>
            <NavLink
              to="/getting-started"
              className={({ isActive }) =>
                isActive ? "text-ink-100" : "text-ink-300 hover:text-ink-100"
              }
            >
              Getting started
            </NavLink>
            {auth.scope === "platform" && (
              <NavLink
                to="/admin/rules"
                className={({ isActive }) =>
                  isActive ? "text-ink-100" : "text-ink-300 hover:text-ink-100"
                }
              >
                Rule Library
              </NavLink>
            )}
            {auth.scope === "platform" && (
              <NavLink
                to="/admin/onboard"
                className={({ isActive }) =>
                  isActive ? "text-ink-100" : "text-ink-300 hover:text-ink-100"
                }
              >
                Add PWS
              </NavLink>
            )}
            {auth.scope === "platform" && (
              <NavLink
                to="/admin/activity"
                className={({ isActive }) =>
                  isActive ? "text-ink-100" : "text-ink-300 hover:text-ink-100"
                }
              >
                Activity
              </NavLink>
            )}
            {auth.scope === "platform" && (
              <NavLink
                to="/admin/tenants"
                className={({ isActive }) =>
                  isActive ? "text-ink-100" : "text-ink-300 hover:text-ink-100"
                }
              >
                Tenants
              </NavLink>
            )}
            {auth.role === "admin" && (
              <NavLink
                to="/admin/invitations"
                className={({ isActive }) =>
                  isActive ? "text-ink-100" : "text-ink-300 hover:text-ink-100"
                }
              >
                Invitations
              </NavLink>
            )}
            {auth.role === "admin" && (
              <NavLink
                to="/admin/users"
                className={({ isActive }) =>
                  isActive ? "text-ink-100" : "text-ink-300 hover:text-ink-100"
                }
              >
                Users
              </NavLink>
            )}
            {auth.scope === "platform" && auth.role === "admin" && (
              <NavLink
                to="/admin/smtp"
                className={({ isActive }) =>
                  isActive ? "text-ink-100" : "text-ink-300 hover:text-ink-100"
                }
              >
                SMTP
              </NavLink>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-3 text-sm">
            <ThemeToggle />
            <NavLink
              to="/account"
              className={({ isActive }) =>
                isActive ? "text-ink-100" : "text-ink-300 hover:text-ink-100"
              }
            >
              Account
            </NavLink>
            <span className="text-ink-300 inline-flex items-center">
              {auth.scope}:{auth.role}
              <InfoTip
                label="Your access level"
                body="Scope is platform (AquaTrack staff — every tenant) or tenant (a single utility). Role is admin (manage users and settings), operator (edit data), or viewer (read-only)."
              />
            </span>
            <button onClick={() => void onLogout()} className="btn-primary py-1 px-2 text-xs">
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6">
        <Outlet />
      </main>
      {idle.warning && <IdleWarningModal onStay={idle.staySignedIn} />}
    </div>
  );
}
